import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { JwtModule } from '@auth0/angular-jwt';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { AuthGuard } from './helpers/auth.guard';
import { ErrorInterceptor } from './helpers/error.interceptor';
import { AuthInterceptor } from './helpers/auth.interceptor';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { ErrorHandlerInterceptor } from './helpers/error-handler.interceptor';
import { forkJoin, map } from 'rxjs';
import { RoleGuard } from './helpers/role.guard';
// PrimeNG
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { definePreset } from '@primeuix/themes';

const Noir = definePreset(Aura, {
  semantic: {
    primary: {
      50:  '{zinc.50}',
      100: '{zinc.100}',
      200: '{zinc.200}',
      300: '{zinc.300}',
      400: '{zinc.400}',
      500: '{zinc.500}',
      600: '{zinc.600}',
      700: '{zinc.700}',
      800: '{zinc.800}',
      900: '{zinc.900}',
      950: '{zinc.950}'
    },
    colorScheme: {
      light: {
        primary: {
          color:        '{zinc.950}',
          inverseColor: '#ffffff',
          hoverColor:   '{zinc.900}',
          activeColor:  '{zinc.800}'
        },
        highlight: {
          background:      '{zinc.950}',
          focusBackground: '{zinc.700}',
          color:           '#ffffff',
          focusColor:      '#ffffff'
        }
      },
      dark: {
        primary: {
          color:        '{zinc.50}',
          inverseColor: '{zinc.950}',
          hoverColor:   '{zinc.100}',
          activeColor:  '{zinc.200}'
        },
        highlight: {
          background:      'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color:           'rgba(255,255,255,.87)',
          focusColor:      'rgba(255,255,255,.87)'
        }
      }
    }
  }
});

export class MultiTranslateLoader implements TranslateLoader {
  private http: HttpClient;

  constructor(
    handler: HttpBackend,
  ) {
    this.http = new HttpClient(handler);
  }

  getTranslation(lang: string) {
    const files = [
      `./assets/i18n/main/${lang}.json`,
      `./assets/i18n/prime/${lang}-prime.json`,
      `./assets/i18n/response/${lang}-response.json`,
    ];

    return forkJoin(files.map(file => this.http.get(file))).pipe(
      map((translations: any[]) => Object.assign({}, ...translations))
    );
  }
}

export function createLoader(handler: HttpBackend) {
  return new MultiTranslateLoader(handler);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    providePrimeNG({
      theme: {
        preset: Noir,
        options: {
          darkModeSelector: false || 'none',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          },
          overlayOptions: {
            appendTo: 'body'
          },
        }
      }
    }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([ErrorHandlerInterceptor]),
    ),
    provideTranslateService({
      fallbackLang: 'es',
      loader: {
        provide: TranslateLoader,
        useFactory: createLoader,
        deps: [HttpBackend],
      },
    }),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    importProvidersFrom(
      JwtModule.forRoot({
        config: {}
      }),
      LoggerModule.forRoot({
        level: NgxLoggerLevel.DEBUG,
        serverLogLevel: NgxLoggerLevel.ERROR,
      })
    ),
    AuthGuard,
    RoleGuard,
    MessageService,
    DialogService
  ]
};
